'use client'

import { ArrowLeft } from 'react-feather'
import { t } from '@lingui/core/macro'

import { MeetingLinkOrder, OrderStatus } from '@/core/graphql/types'
import { Link } from '@/lib/next-shim'

import { useCancelOrder } from '../model/useCancelOrder'
import { OrderHeader } from './OrderHeader'
import { AppointmentDetailsCard } from './AppointmentDetailsCard'
import { DocumentsCard } from './DocumentsCard'
import { DoctorCard } from './DoctorCard'
import { HelpCard } from './HelpCard'

type OrderDetailsProps = {
  order: MeetingLinkOrder
  referenceId: string
  ordersSectionTitle: string
  filesLabel: string
  cancelButtonLabel: string
}

export function OrderDetails({ order, referenceId, ordersSectionTitle, filesLabel, cancelButtonLabel }: OrderDetailsProps) {
  const canCancel = order.status === OrderStatus.Draft || order.status === OrderStatus.Pending
  const [cancelOrder, isCanceling] = useCancelOrder(order._id)

  return (
    <div className="mx-auto w-full max-w-5xl animate-in fade-in px-4 py-8 duration-500 sm:px-6 lg:px-8">
      <Link
        href="/account/orders"
        className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 transition-colors hover:text-primary"
      >
        <ArrowLeft size={16} aria-hidden="true" />
        {t`Back to orders`}
      </Link>

      <div className="mb-8">
        <OrderHeader
          order={order}
          referenceId={referenceId}
          ordersSectionTitle={ordersSectionTitle}
          cancelButtonLabel={cancelButtonLabel}
          canCancel={canCancel}
          onCancel={cancelOrder}
          isCanceling={isCanceling}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {/* LEFT COLUMN: appointment + documents */}
        <div className="flex flex-col gap-6 md:col-span-2">
          <AppointmentDetailsCard order={order} />
          {order.files && order.files.length > 0 && <DocumentsCard files={order.files} filesLabel={filesLabel} />}
        </div>

        {/* RIGHT COLUMN: doctor + help */}
        <div className="flex flex-col gap-6">
          <DoctorCard order={order} />
          <HelpCard referenceId={referenceId} />
        </div>
      </div>
    </div>
  )
}
